/**
 * 画面の「ウィンドウ」（3D の上の表示・全画面のときのツールバーとパネル）の配置。端末ごとに localStorage に残す
 * （コースの保存データには入れない。描画品質の設定と同じ扱い）。DOM・React に依存しない。
 */

/** 動かせるウィンドウ。 */
export const WINDOW_IDS = [
  'hud',
  'views',
  'playback',
  'legend',
  'section',
  'toolbar',
  'left',
  'right',
  'bottom',
  'menu',
] as const;

export type WindowId = (typeof WINDOW_IDS)[number];

/** 全画面のときだけ、浮かぶウィンドウとして表示・非表示を切り替えるもの（ツールバーとパネル）。 */
export const FLOATING_IDS = ['toolbar', 'left', 'right', 'bottom'] as const;

export type FloatingId = (typeof FLOATING_IDS)[number];

/** 全画面のときに、まとめて隠せる 3D の上の表示（既定は出している）。 */
export const OVERLAY_IDS = ['hud', 'views', 'playback', 'legend', 'section'] as const;

export type OverlayId = (typeof OVERLAY_IDS)[number];

/** 配置を別々に覚える画面の状態。 */
export type LayoutKind = 'normal' | 'fullscreen';

/**
 * ウィンドウの位置。入れ物（3D ビュー・画面）の中で動かせる余白に対する割合（0〜1）。
 * x = 0 は左端、1 は右端に寄せた位置。割合で持つので、画面の大きさ・向きが変わっても、はみ出さない。
 */
export interface WindowPlacement {
  readonly x: number;
  readonly y: number;
}

/** 配置の全体。 */
export interface WindowLayout {
  readonly normal: Readonly<Partial<Record<WindowId, WindowPlacement>>>;
  readonly fullscreen: Readonly<Partial<Record<WindowId, WindowPlacement>>>;
  /** 全画面のときに出しておくウィンドウ（ツールバー・パネル） */
  readonly shown: Readonly<Partial<Record<FloatingId, boolean>>>;
  /** 全画面のときに隠している 3D の上の表示（HUD・視点・再生の操作など） */
  readonly hidden: Readonly<Partial<Record<OverlayId, boolean>>>;
  /** 全画面の操作を、小さなボタン 1 つにたたんでいるか */
  readonly collapsed: boolean;
}

/** 何も動かしていない配置。 */
export const DEFAULT_LAYOUT: WindowLayout = {
  normal: {},
  fullscreen: {},
  shown: {},
  hidden: {},
  collapsed: false,
};

/** 配置を保存する localStorage のキー。 */
export const LAYOUT_STORAGE_KEY = 'rcs.settings.windowLayout';

/** 大きさ（px）。 */
export interface Size {
  readonly width: number;
  readonly height: number;
}

/** 入れ物の中での位置（左上、px）。 */
export interface Offset {
  readonly left: number;
  readonly top: number;
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/** 位置（割合）を、入れ物の中の左上の位置（px）にする。ウィンドウが入れ物より大きければ 0。 */
export function offsetOf(placement: WindowPlacement, size: Size, container: Size): Offset {
  const freeX = Math.max(0, container.width - size.width);
  const freeY = Math.max(0, container.height - size.height);
  return { left: clamp01(placement.x) * freeX, top: clamp01(placement.y) * freeY };
}

/** 入れ物の中の左上の位置（px）を、位置（割合）にする。入れ物からはみ出す位置は、端に寄せる。 */
export function placementOf(offset: Offset, size: Size, container: Size): WindowPlacement {
  const freeX = Math.max(0, container.width - size.width);
  const freeY = Math.max(0, container.height - size.height);
  return {
    x: freeX > 0 ? clamp01(offset.left / freeX) : 0,
    y: freeY > 0 ? clamp01(offset.top / freeY) : 0,
  };
}

const isPlacement = (value: unknown): value is WindowPlacement =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as { x?: unknown }).x === 'number' &&
  typeof (value as { y?: unknown }).y === 'number' &&
  Number.isFinite((value as { x: number }).x) &&
  Number.isFinite((value as { y: number }).y);

function parsePlacements(value: unknown): Partial<Record<WindowId, WindowPlacement>> {
  const result: Partial<Record<WindowId, WindowPlacement>> = {};
  if (typeof value !== 'object' || value === null) return result;
  for (const id of WINDOW_IDS) {
    const placement = (value as Record<string, unknown>)[id];
    if (isPlacement(placement)) result[id] = { x: clamp01(placement.x), y: clamp01(placement.y) };
  }
  return result;
}

/** 保存された配置を読む（JSON の文字列）。不正な部分は捨てる。 */
export function parseLayout(text: string | null): WindowLayout {
  if (text === null) return DEFAULT_LAYOUT;
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    return DEFAULT_LAYOUT;
  }
  if (typeof value !== 'object' || value === null) return DEFAULT_LAYOUT;
  const record = value as Record<string, unknown>;
  const shown: Partial<Record<FloatingId, boolean>> = {};
  if (typeof record.shown === 'object' && record.shown !== null) {
    for (const id of FLOATING_IDS) {
      const flag = (record.shown as Record<string, unknown>)[id];
      if (typeof flag === 'boolean') shown[id] = flag;
    }
  }
  const hidden: Partial<Record<OverlayId, boolean>> = {};
  if (typeof record.hidden === 'object' && record.hidden !== null) {
    for (const id of OVERLAY_IDS) {
      const flag = (record.hidden as Record<string, unknown>)[id];
      if (typeof flag === 'boolean') hidden[id] = flag;
    }
  }
  return {
    normal: parsePlacements(record.normal),
    fullscreen: parsePlacements(record.fullscreen),
    shown,
    hidden,
    collapsed: record.collapsed === true,
  };
}

/** 保存先。`localStorage` の一部だけを使う（テストで差し替えられる）。 */
export type LayoutStorage = Pick<Storage, 'getItem' | 'setItem'>;

/** 保存された配置を読む。保存がない・読めないときは、何も動かしていない配置。 */
export function loadLayout(storage?: LayoutStorage): WindowLayout {
  try {
    return parseLayout((storage ?? localStorage).getItem(LAYOUT_STORAGE_KEY));
  } catch {
    // プライベートブラウズなどで localStorage を使えない場合は、保存なしで動く
    return DEFAULT_LAYOUT;
  }
}

/** 配置を保存する。保存できなくても、アプリの動作には影響しない。 */
export function saveLayout(layout: WindowLayout, storage?: LayoutStorage): void {
  try {
    (storage ?? localStorage).setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  } catch {
    // 保存できない環境では、その回の配置だけが有効になる
  }
}

/** ウィンドウの位置を変えた配置。 */
export function withPlacement(
  layout: WindowLayout,
  kind: LayoutKind,
  id: WindowId,
  placement: WindowPlacement,
): WindowLayout {
  return { ...layout, [kind]: { ...layout[kind], [id]: placement } };
}

/** 画面の状態 `kind` の配置を、元に戻した配置（全画面で出しておくウィンドウも戻す）。 */
export function withReset(layout: WindowLayout, kind: LayoutKind): WindowLayout {
  return kind === 'fullscreen'
    ? { ...layout, fullscreen: {}, shown: {}, hidden: {}, collapsed: false }
    : { ...layout, normal: {} };
}

/** 全画面のときに出しておくウィンドウを切り替えた配置。 */
export function withShown(layout: WindowLayout, id: FloatingId, shown: boolean): WindowLayout {
  return { ...layout, shown: { ...layout.shown, [id]: shown } };
}

/** 全画面のときに、ウィンドウを出しておくか（既定は出さない。3D を広く見せる）。 */
export function isShown(layout: WindowLayout, id: FloatingId): boolean {
  return layout.shown[id] ?? false;
}

/** 全画面のときに、3D の上の表示を出しているか（既定は出している）。 */
export function isOverlayShown(layout: WindowLayout, id: OverlayId): boolean {
  return !(layout.hidden[id] ?? false);
}

/** 全画面のときの、3D の上の表示を 1 つ、出す・隠す。 */
export function withOverlay(layout: WindowLayout, id: OverlayId, shown: boolean): WindowLayout {
  return { ...layout, hidden: { ...layout.hidden, [id]: !shown } };
}

/** 全画面の操作をたたむ・開く。 */
export function withCollapsed(layout: WindowLayout, collapsed: boolean): WindowLayout {
  return { ...layout, collapsed };
}

/** 画面をすっきりさせる：3D の上の表示・ツールバー・パネルをすべて隠し、全画面の操作をたたむ。 */
export function withAllHidden(layout: WindowLayout): WindowLayout {
  return {
    ...layout,
    hidden: Object.fromEntries(OVERLAY_IDS.map((id) => [id, true])),
    shown: Object.fromEntries(FLOATING_IDS.map((id) => [id, false])),
    collapsed: true,
  };
}

/** すっきりさせた表示を戻す：3D の上の表示をすべて出し、全画面の操作を開く（ツールバー・パネルは隠したまま）。 */
export function withAllShown(layout: WindowLayout): WindowLayout {
  return { ...layout, hidden: {}, collapsed: false };
}

/** 全画面で、すっきりさせているか（3D の上の表示がすべて隠れ、操作がたたまれている）。 */
export function isClean(layout: WindowLayout): boolean {
  return layout.collapsed && OVERLAY_IDS.every((id) => layout.hidden[id] === true);
}
